/* TODO: t() */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { Component } from 'react';
import { View, StyleSheet } from 'react-native';

import { useExport } from '../../support/useExport';

import {
  Checkbox,
  ExportModal,
  FilterablePicker,
  MarketSelector,
  Text,
} from '../uiold';
import { themeColors } from '../uiold/Constants';
import FormSection from '../VendorList/FormSection';
import mapToPlainObject from '../../support/mapToPlainObject';

import { Status } from '../../types/shared/Attendance';
import { Market } from '../../types/shared/Market';
import { VendorSummaryWithMarkets } from '../../types/shared/Vendor';
import { Option } from '../../runtypes/SignupFormDefinitionRuntype';

type OptionsContent = string | { fieldID: string; name: string };

type Tag = {
  id: string;
  name: string;
  color: string;
};

type AttendanceByDate = {
  [date: string]: Status;
};

type AttendanceByMarket = {
  [marketID: string]: AttendanceByDate;
};

export type VendorAttendance = VendorSummaryWithMarkets & {
  attendance: AttendanceByMarket;
};

type Props = {
  tags: Array<Tag>;
  categories: Array<Option>;
  markets: Array<Market>;
  vendors: Array<VendorAttendance> | null | undefined;
  closeExportModal: () => void;
};

type State = {
  isLoading: boolean;
  selectedMarketDates: Map<string, Set<string>>;
  selectedVendors: Set<string>;
  selectedCategories: Set<string>;
  selectedTags: Set<string>;
  selectedStatuses: Set<string>;
  showInternalNotes: boolean;
  filterText: string;
  vendorCount: number;
};

const availableStatuses = ['Present', 'No Show', 'Late', 'Excused'];

// Using this higher order component to implement hooks without refactoring
// ExportAttendance
export default function ExportAttendanceWithHooks(props: Props) {
  const [exportAttendance] = useExport('/api/attendance/export');
  return <ExportAttendance {...props} exportAttendance={exportAttendance} />;
}

class ExportAttendance extends Component<
  Props & { exportAttendance: (body?: unknown) => void },
  State
> {
  state: State = {
    isLoading: false,
    selectedMarketDates: new Map(),
    selectedCategories: new Set(
      this.props.categories.map((option) => option.id),
    ) as Set<string>,
    selectedVendors: new Set(getAllVendors(this.props.vendors)) as Set<string>,
    selectedTags: new Set() as Set<string>,
    selectedStatuses: new Set(availableStatuses) as Set<string>,
    showInternalNotes: true,
    filterText: '',
    vendorCount: 0,
  };

  componentDidMount() {
    this.setState({ vendorCount: countVendor(this.state, this.props.vendors) });
  }

  render() {
    let { isLoading } = this.state;
    return (
      <ExportModal
        title="Export Vendor Attendance"
        isLoading={isLoading}
        onSubmit={this._exportToCsv}
        onClose={this._onModalClosePress}
      >
        <>
          {this._renderMarketDatesFormField()}
          <FormSection
            zIndex={6}
            title="Vendors"
            subtitle="Exports attendance with selected vendors"
          >
            {this._renderVendorCheckboxes()}
          </FormSection>
          <FormSection
            zIndex={5}
            title="Statuses"
            subtitle="Exports attendance with selected statuses"
          >
            {this._renderStatusCheckboxes()}
          </FormSection>
          <FormSection
            zIndex={4}
            title="Categories"
            subtitle="Exports attendance with selected categories"
          >
            {this._renderCategoriesCheckboxes()}
          </FormSection>
          <FormSection
            zIndex={3}
            title="Tags"
            subtitle="Exports attendance with selected tags"
          >
            {this._renderTagsCheckboxes()}
          </FormSection>
          <FormSection
            zIndex={2}
            title="Internal Notes"
            subtitle="Show internal notes as additional column"
          >
            <Checkbox
              isChecked={this.state.showInternalNotes}
              text="Internal Notes"
              onChange={this._showInternalNotes}
            />
          </FormSection>
        </>
      </ExportModal>
    );
  }

  _renderMarketDatesFormField = () => {
    return (
      <View style={styles.formSection}>
        <Text style={styles.paneHeader}>Markets and Dates</Text>
        <Text type="label" style={styles.paneText}>
          Export attendance with selected markets and dates
        </Text>
        {this._renderMarketDatesCheckboxes()}
      </View>
    );
  };

  _renderMarketDatesCheckboxes = (): React.ReactNode => {
    let { selectedMarketDates } = this.state;
    let { markets } = this.props;
    return markets.map((market) => (
      <MarketSelector
        key={market.id}
        marketTitle={market.name}
        dates={market.dates}
        selectedItems={selectedMarketDates.get(market.id) || new Set()}
        onSelectAll={() => {
          this._selectAllMarketDates(market.id);
        }}
        onChange={(selectedKey: string) =>
          this._updateColumnMarketDates(market.id, selectedKey)
        }
      />
    ));
  };

  _renderVendorCheckboxes = () => {
    let { selectedVendors, filterText } = this.state;
    let { vendors } = this.props;

    if (!vendors) {
      return;
    }

    let vendorOptions: Array<OptionsContent> = [];
    vendors.forEach((vendor) => {
      vendor.name &&
        vendorOptions.push({ fieldID: vendor.id, name: vendor.name });
    });

    return (
      <FilterablePicker
        options={vendorOptions}
        filterText={filterText}
        selectedItem={selectedVendors}
        onFilterTextChange={(filterText: string) =>
          this.setState({ filterText })
        }
        onSelectAll={() => {
          this._selectAll('selectedVendors');
        }}
        onChange={(selectedKey: string) =>
          this._updateColumnString('selectedVendors', selectedKey)
        }
      />
    );
  };

  _renderCategoriesCheckboxes = () => {
    let { selectedCategories, filterText } = this.state;
    let { categories } = this.props;

    return (
      <FilterablePicker
        options={categories.map((option) => {
          return { fieldID: option.id, name: option.name };
        })}
        filterText={filterText}
        selectedItem={selectedCategories}
        onFilterTextChange={(filterText: string) =>
          this.setState({ filterText })
        }
        onSelectAll={() => {
          this._selectAll('selectedCategories');
        }}
        onChange={(selectedKey: string) =>
          this._updateColumnString('selectedCategories', selectedKey)
        }
      />
    );
  };

  _renderStatusCheckboxes = () => {
    let { selectedStatuses, filterText } = this.state;
    return (
      <FilterablePicker
        options={availableStatuses}
        filterText={filterText}
        selectedItem={selectedStatuses}
        onFilterTextChange={(filterText: string) =>
          this.setState({ filterText })
        }
        onSelectAll={() => {
          this._selectAll('selectedStatuses');
        }}
        onChange={(selectedKey: string) =>
          this._updateColumnString('selectedStatuses', selectedKey)
        }
      />
    );
  };

  _renderTagsCheckboxes = () => {
    let { selectedTags, filterText } = this.state;
    let { tags } = this.props;

    let tagOptions: Array<OptionsContent> = tags.map((tag) => {
      return { fieldID: tag.id, name: tag.name };
    });

    return (
      <FilterablePicker
        options={tagOptions}
        filterText={filterText}
        selectedItem={selectedTags}
        onFilterTextChange={(filterText: string) =>
          this.setState({ filterText })
        }
        onSelectAll={() => {
          this._selectAll('selectedTags');
        }}
        onChange={(selectedKey: string) =>
          this._updateColumnString('selectedTags', selectedKey)
        }
      />
    );
  };

  _showInternalNotes = () => {
    this.setState((state) => {
      return { showInternalNotes: !state.showInternalNotes };
    });
  };

  _updateColumnString = (
    columnName:
      | 'selectedVendors'
      | 'selectedCategories'
      | 'selectedTags'
      | 'selectedStatuses',
    selection: string,
  ) => {
    let oldSet = this.state[columnName];
    let newSet = new Set(oldSet);
    newSet.has(selection) ? newSet.delete(selection) : newSet.add(selection);
    let vendorCount = countVendor(
      { ...this.state, [columnName]: newSet },
      this.props.vendors,
    );
    this.setState({ [columnName]: newSet, vendorCount } as any);
  };

  _updateColumnMarketDates = (marketID: string, selection: string) => {
    let { selectedMarketDates } = this.state;
    let oldSet = selectedMarketDates.get(marketID);
    let newSet = new Set(oldSet);
    newSet.has(selection) ? newSet.delete(selection) : newSet.add(selection);

    let oldMap = new Map(selectedMarketDates);
    let newMap = oldMap.set(marketID, newSet);

    let vendorCount = countVendor(
      {
        ...this.state,
        selectedMarketDates: newMap,
      },
      this.props.vendors,
    );
    this.setState({ selectedMarketDates: newMap, vendorCount });
  };

  _selectAllMarketDates = (marketID: string) => {
    let allMarketDates = getAllMarketDates(this.props.markets);

    let { selectedMarketDates } = this.state;
    let oldSet = new Set(selectedMarketDates.get(marketID));
    let newSet =
      oldSet.size > 0
        ? new Set<string>()
        : new Set(allMarketDates.get(marketID));

    let oldMap = new Map(selectedMarketDates);
    let newMap = oldMap.set(marketID, newSet);

    let vendorCount = countVendor(
      {
        ...this.state,
        selectedMarketDates: newMap,
      },
      this.props.vendors,
    );
    this.setState({ selectedMarketDates: newMap, vendorCount });
  };

  _selectAll = (
    columnName:
      | 'selectedVendors'
      | 'selectedCategories'
      | 'selectedTags'
      | 'selectedStatuses',
  ) => {
    let oldSet = this.state[columnName];
    let newSet =
      oldSet.size > 0 ? new Set() : new Set(this._selectGetter(columnName));

    let vendorCount = countVendor(
      {
        ...this.state,
        [columnName]: newSet,
      },
      this.props.vendors,
    );
    this.setState({ [columnName]: newSet, vendorCount } as any);
  };

  _selectGetter = (columnName: string) => {
    switch (columnName) {
      case 'selectedStatuses':
        return new Set(availableStatuses) as Set<string>;
      case 'selectedVendors':
        return new Set(getAllVendors(this.props.vendors)) as Set<string>;
      case 'selectedCategories':
        return new Set(
          this.props.categories.map((option) => option.id),
        ) as Set<string>;
      case 'selectedTags':
        return this.props.tags.map((tag) => tag.id);
      default:
        return new Set() as Set<string>;
    }
  };

  _exportToCsv = () => {
    let {
      selectedMarketDates,
      selectedCategories,
      selectedVendors,
      selectedStatuses,
      selectedTags,
      showInternalNotes,
    } = this.state;
    let { categories, exportAttendance } = this.props;
    const categoryNamesById = categories.reduce<{
      [categoryId: string]: string;
    }>((prev, curr) => {
      return { ...prev, [curr.id]: curr.name };
    }, {});
    let data = {
      selectedMarketDates: mapToPlainObject(selectedMarketDates, (dates) =>
        Array.from(dates),
      ),
      selectedCategories: Array.from(selectedCategories),
      selectedVendors: Array.from(selectedVendors),
      selectedStatuses: Array.from(selectedStatuses),
      selectedTags: Array.from(selectedTags),
      displayNotes: showInternalNotes,
      categoryNamesById: categoryNamesById,
    };
    exportAttendance(data);
  };

  _onModalClosePress = () => {
    this.props.closeExportModal();
  };
}

function getAllMarketDates(markets: Array<Market>): Map<string, Set<string>> {
  let marketDatesMap = new Map();
  markets.forEach((market) => {
    marketDatesMap.set(market.id, new Set(market.dates));
  });
  return marketDatesMap;
}

function getAllVendors(
  vendors: Array<VendorAttendance> | null | undefined,
): Array<string> {
  let allVendor: Array<string> = [];
  vendors &&
    vendors.forEach((vendor) => {
      allVendor.push(vendor.id);
    });
  return allVendor;
}

function countVendor(
  newState: State,
  vendors: Array<VendorAttendance> | null | undefined,
): number {
  let {
    selectedMarketDates,
    selectedCategories,
    selectedVendors,
    selectedTags,
    selectedStatuses,
  } = newState;

  let vendorCount = 0;

  if (vendors) {
    vendors.forEach((vendor) => {
      if (!selectedVendors.has(vendor.id)) {
        return;
      }
      if (
        !Array.isArray(vendor.category) ||
        !vendor.category.some((category) => selectedCategories.has(category))
      ) {
        return;
      }
      if (selectedMarketDates.size > 0) {
        let marketAttendance = false;
        Object.keys(vendor.attendance).forEach((marketID) => {
          if (selectedMarketDates.has(marketID)) {
            let marketDates = vendor.attendance[marketID] || {};
            let filteredDates = new Set(selectedMarketDates.get(marketID));
            if (
              Object.keys(marketDates).some(
                (date) =>
                  filteredDates.has(date) &&
                  selectedStatuses.has(marketDates[date] ?? ''),
              )
            ) {
              marketAttendance = true;
            }
          }
        });
        if (!marketAttendance) {
          return;
        }
      }
      if (
        selectedTags.size > 0 &&
        (!Array.isArray(vendor.tags) ||
          (vendor.tags.length > 0 &&
            !vendor.tags.some((tag) => selectedTags.has(tag))))
      ) {
        return;
      }
      vendorCount += 1;
    });
  }
  return vendorCount;
}

let styles = StyleSheet.create({
  formSection: {
    paddingHorizontal: 30,
    paddingTop: 10,
    paddingBottom: 20,
    borderBottomWidth: StyleSheet.hairlineWidth * 2,
    borderColor: themeColors.lightGrey,
  },
  paneHeader: {
    marginBottom: 8,
    fontWeight: '600',
    color: themeColors.darkGrey,
  },
  paneText: {
    color: themeColors.darkGrey,
  },
});
